import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Input, FormGroup, Label, Form, Row, Col, Card, CardBody, InputGroup, InputGroupText } from "reactstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

const EditInvoiceTDS = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [invoices, setInvoices] = useState([]);
    const [inputMode, setInputMode] = useState("percentage");

    const [formData, setFormData] = useState({
        id: id,
        invoice_id: "",
        tds_percentage: "",
        tds_amount: "",
        note: ""
    });

    const [errors, setErrors] = useState({});

    // styling for React Select
    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            minHeight: '45px',
            borderRadius: '8px',
        }),
        menuPortal: base => ({ ...base, zIndex: 9999 })
    };

    useEffect(() => {
        const initPage = async () => {
            dispatch(loadingTrue());
            await fetchInvoices();
            await fetchTdsDetails();
            dispatch(loadingFalse());
        };
        if (id) initPage();
    }, [id]);

    const fetchInvoices = async () => {
        try {
            const res = await Handler({ method: "GET", url: "/invoice/list.php" });
            if (res?.success) {
                // Machan inga summary nested-ah irundha adha handle panniruken
                const options = res.data.map(item => ({
                    value: item.invoice?.id || item.id,
                    label: `${item.invoice?.invoice_no || item.invoice_no} - ${item.client?.name} (Amt: ₹${Math.round(item.summary?.total_amount || item.total_amount || 0)})`
                }));
                setInvoices(options);
            }
        } catch (err) { console.error("Invoice fetch error"); }
    };

    const fetchTdsDetails = async () => {
        try {
            const res = await Handler({ method: "GET", url: `/tds/view.php?id=${id}` });
            if (res?.success) {
                const d = res.data;
                // 🔥 Nee anupuna JSON structure padi pathu mapping panniten machan
                setFormData({
                    id: d.tds?.id,
                    invoice_id: d.invoice?.id,
                    tds_percentage: d.tds?.percentage || "",
                    tds_amount: d.tds?.amount || "",
                    note: d.tds?.note || ""
                });

                // Mode logic
                const pct = d.tds?.percentage;
                if (pct && parseFloat(pct) > 0) {
                    setInputMode("percentage");
                } else {
                    setInputMode("amount");
                }
                 dispatch(loadingFalse());
            }
        } catch (err) {
            toast.error("Error loading details");
             dispatch(loadingFalse());
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: "" });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.invoice_id) return toast.error("Please select an invoice");

        dispatch(loadingTrue());
        setErrors({});

        const payload = {
            id: formData.id,
            invoice_id: formData.invoice_id,
            tds_percentage: inputMode === "percentage" ? formData.tds_percentage : null,
            tds_amount: inputMode === "amount" ? formData.tds_amount : null,
            note: formData.note
        };

        try {
            const res = await Handler({
                method: "POST",
                url: "/tds/update.php",
                data: payload
            });

            if (res?.success) {
                Swal.fire("Updated!", "TDS record updated successfully", "success");
                navigate("/tds"); 
            } else {
                setErrors(res?.errors || {});
                toast.error(res?.message || "Update failed");
            }
        } catch (err) {
            Swal.fire("Error", "Server error", "error");
        } finally {
            dispatch(loadingFalse());
        }
    };

    return (
        <div className="page-container p-1 bg-light min-vh-100">
            <div className="page-header mb-4">
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h3 className="fw-bold text-theme m-0">Edit TDS Record</h3>
                        <p className="text-muted small m-0">Update details for Record ID: #{id}</p>
                    </div>
                    <Button color="white" className="shadow-sm border-0 rounded-pill px-4" onClick={() => navigate(-1)}>
                        <i className="bx bx-arrow-back me-1"></i> Back
                    </Button>
                </div>
            </div>

            <Card className="border-0 shadow-sm rounded-4">
                <CardBody className="p-4">
                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Col md={12}>
                                <FormGroup>
                                    <Label className="small fw-bold opacity-75">TARGET INVOICE</Label>
                                    <Select
                                        options={invoices}
                                        styles={customSelectStyles}
                                        placeholder="Select Invoice..."
                                        // 🔥 Inga thaan logic: string vs number issue varaama iruka '==' use panniten
                                        value={invoices.find(opt => opt.value == formData.invoice_id) || null}
                                        onChange={(opt) => setFormData({ ...formData, invoice_id: opt?.value })}
                                        isClearable
                                        menuPortalTarget={document.body}
                                    />
                                    {errors.invoice_id && <div className="text-danger small mt-1">{errors.invoice_id}</div>}
                                </FormGroup>
                            </Col>
                        </Row>

                        <div className="bg-light p-3 rounded-3 mb-4 mt-2">
                            <Label className="small fw-bold opacity-75 d-block mb-3 text-uppercase">Update Method</Label>
                            <div className="d-flex gap-4">
                                <FormGroup check>
                                    <Label check className="pointer">
                                        <Input type="radio" name="mode" checked={inputMode === "percentage"} onChange={() => setInputMode("percentage")} /> 
                                        By Percentage (%)
                                    </Label>
                                </FormGroup>
                                <FormGroup check>
                                    <Label check className="pointer">
                                        <Input type="radio" name="mode" checked={inputMode === "amount"} onChange={() => setInputMode("amount")} /> 
                                        By Amount (₹)
                                    </Label>
                                </FormGroup>
                            </div>
                        </div>

                        <Row>
                            {inputMode === "percentage" ? (
                                <Col md={6}>
                                    <FormGroup>
                                        <Label className="small fw-bold">TDS Percentage (%)</Label>
                                        <InputGroup>
                                            <Input
                                                type="number"
                                                name="tds_percentage"
                                                placeholder="e.g. 10"
                                                step="any"
                                                value={formData.tds_percentage}
                                                onChange={handleChange}
                                                className={`fw-bold ${errors.tds_percentage ? "is-invalid" : ""}`}
                                            />
                                            <InputGroupText className="bg-white">%</InputGroupText>
                                        </InputGroup>
                                    </FormGroup>
                                </Col>
                            ) : (
                                <Col md={6}>
                                    <FormGroup>
                                        <Label className="small fw-bold">TDS Amount (₹)</Label>
                                        <InputGroup>
                                            <InputGroupText className="bg-white text-danger fw-bold">₹</InputGroupText>
                                            <Input
                                                type="number"
                                                name="tds_amount"
                                                placeholder="0.00"
                                                step="any"
                                                value={formData.tds_amount}
                                                onChange={handleChange}
                                                className={`fw-bold ${errors.tds_amount ? "is-invalid" : ""}`}
                                            />
                                        </InputGroup>
                                    </FormGroup>
                                </Col>
                            )}
                        </Row>

                        <Row className="mt-3">
                            <Col md={12}>
                                <FormGroup>
                                    <Label className="small fw-bold opacity-75">NOTE / REMARKS</Label>
                                    <Input 
                                        type="textarea" 
                                        name="note" 
                                        rows="3" 
                                        value={formData.note} 
                                        onChange={handleChange} 
                                        placeholder="Add remarks here..."
                                        style={{ borderRadius: '10px' }}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <div className="mt-4 pt-3 border-top d-flex justify-content-end gap-2">
                            <Button color="light" className="px-4" onClick={() => navigate(-1)}>Cancel</Button>
                            <Button type="submit" className="add-btn px-5 shadow">
                                <i className="bx bx-save me-1"></i> Update Changes
                            </Button>
                        </div>
                    </Form>
                </CardBody>
            </Card>
        </div>
    );
};

export default EditInvoiceTDS;